"use client";
import Link from "next/link";
import React, { useState } from "react";
import { motion } from "framer-motion";
import { fadeIn } from "../../utils/motion";
import LoginAside from "../authSection/loginAside";
import { RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { selectUser } from "../../redux/userDetailsSlice";
import SetProfileForNewUser from "../setProfile";
import { setShowProfileForm } from "../../redux/authSlice";

// Define product type
interface Product {
  background: string;
  image: string;
  title: string;
  link: string;
}

// Define product list
const products: Product[] = [
  {
    background: "/Light_21.png",
    image: "https://d2fbpyhlah02sy.cloudfront.net/product/gold/2gm/Group+25.png",
    title: "5 Gram Gold Coin",
    link: "/coins/5-Gram-Gold-Coin",
  },
  {
    background: "https://brightdigigold.s3.ap-south-1.amazonaws.com/Star_3.webp",
    image: "/BanyanTree1.png",
    title: "10 Gram Banyan Tree",
    link: "/coins/10-Gram-Banyan-Tree-Silver-Coin",
  },
  {
    background: "/Light_21.png",
    image: "https://d2fbpyhlah02sy.cloudfront.net/product/gold/2gm/Group+25.png",
    title: "10 Gram Gold Coin",
    link: "/coins/10-Gram-Gold-Coin",
  },
  {
    background: "https://brightdigigold.s3.ap-south-1.amazonaws.com/Star_3.webp",
    image: "https://brightdigigold.s3.ap-south-1.amazonaws.com/bdgCoins/Shree_Ram_Lalla_Colour_Coin_50gm-1727161170301.png",
    title: "20 Gram Ram Lalla Color Silver Coin",
    link: "/coins/20-Gram-Ram-Lalla-Color-Silver-Coin",
  },
];

// Reusable ProductCard Component
const ProductCard = ({
  background,
  image,
  title,
  link,
  delay,
  handleClick,
}: Product & { delay: number; handleClick: () => void }) => {
  return (
    <motion.div
      variants={fadeIn("right", "spring", delay, 0.25)}
      className="bg-themeLight rounded-lg shadow-xl p-4 relative hover:shadow-lg hover:shadow-gray-100 product001 overflow-hidden cursor-pointer"
      onClick={handleClick} // Make entire card clickable
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 w-full h-full bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${background})`, zIndex: 0 }}
      />

      {/* Content */}
      <div className="relative text-center mx-auto mt-10">
        <Image
          src={image}
          alt={title}
          width={250}
          height={250}
          className="mx-auto w-full max-w-[250px] h-auto"
          sizes="(max-width: 768px) 50vw, 25vw"
          loading="lazy"
        />
        <p className="mt-6 text-xs sm:text-base md:text-sm lg:text-md xl:text-lg text-white font-bold">
          {title}
        </p>
      </div>

      <button className="bg-themeBlue w-full block rounded-full py-2 mt-6 text-center">
        <p className="text-xl font-semibold">View</p>
      </button>
    </motion.div>
  );
};

// Main Products Component
const Products = () => {
  const [openLoginAside, setOpenLoginAside] = useState(false);
  const router = useRouter();
  const dispatch = useDispatch();

  // Redux State
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  const showProfileForm = useSelector((state: RootState) => state.auth.showProfileForm);
  const devotee_isNewUser = useSelector((state: RootState) => state.auth.devotee_isNewUser);
  const isLoggedInForTempleReceipt = useSelector((state: RootState) => state.auth.isLoggedInForTempleReceipt);
  const user = useSelector(selectUser);

  // Handle Login
  const handleLoginClick = () => setOpenLoginAside(!openLoginAside);
  const onClose = () => dispatch(setShowProfileForm(false));

  // Handle Card Click
  const handleCardClick = (link: string) => {

    if (isLoggedIn) {
      router.push(link);
    } else if (!isLoggedIn && !isLoggedInForTempleReceipt) {
      // console.log("Opening login aside");
      handleLoginClick();
    } else if (isLoggedInForTempleReceipt && devotee_isNewUser) {
      // console.log("Showing profile form for new user");
      dispatch(setShowProfileForm(true));
    } else if (!user?.data?.isBasicDetailsCompleted) {
      // console.log("Showing profile form for incomplete details");
      dispatch(setShowProfileForm(true));
    } else {
      // console.log("No condition matched for redirect");
    }
  };

  return (
    <div className="bg-theme">
      {openLoginAside && <LoginAside isOpen={openLoginAside} onClose={() => setOpenLoginAside(false)} />}
      {showProfileForm && <SetProfileForNewUser isOpen={showProfileForm} onClose={onClose} />}

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: false, amount: 0.25 }}
        className="mx-auto px-4 sm:px-6 lg:px-16 py-16"
      >
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-white text-3xl sm:text-5xl extrabold">Our Coins</h1>
          <Link
            href="/coins"
            className="bg-themeLight px-3 py-1 text-md text-white rounded border border-gray-500"
          >
            View All
          </Link>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {products.map((product, index) => (
            <ProductCard
              key={product.link}
              background={product.background}
              image={product.image}
              title={product.title}
              link={product.link}
              delay={index * 0.25}
              handleClick={() => handleCardClick(product.link)}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Products;